'use client';

import { ApolloLink, HttpLink } from '@apollo/client';
import {
  ApolloClient,
  ApolloNextAppProvider,
  InMemoryCache,
  SSRMultipartLink,
} from '@apollo/experimental-nextjs-app-support';
import { env } from 'next-runtime-env';

import { getAuthToken } from './auth';

// have a function to create a client for you
function makeClient() {
  const httpLink = new HttpLink({
    // this needs to be an absolute url, as relative urls cannot be used in SSR
    uri: env('NEXT_PUBLIC_GRAPHQL_URL'),
    // you can disable result caching here if you want to
    // (this does not work if you are rendering your page with `export const dynamic = "force-static"`)
    fetchOptions: { cache: 'no-store' },
    // you can override the default `fetchOptions` on a per query basis
    // via the `context` property on the options passed as a second argument
    // to an Apollo Client data fetching hook, e.g.:
    // const { data } = useSuspenseQuery(MY_QUERY, { context: { fetchOptions: { cache: "force-cache" }}});
  });

  // Define a middleware link to attach the authorization token
  const authLink = new ApolloLink((operation, forward) => {
    // Get the token from localStorage
    const token = typeof window !== 'undefined' ? getAuthToken() : null;

    // If a token exists, set the authorization header
    if (token) {
      operation.setContext({
        headers: {
          authorization: `Bearer ${token}`,
        },
      });
    }

    // Pass the operation to the next link in the chain
    return forward(operation);
  });

  // use the `ApolloClient` from "@apollo/experimental-nextjs-app-support"
  return new ApolloClient({
    // use the `InMemoryCache` from "@apollo/experimental-nextjs-app-support"
    cache: new InMemoryCache(),
    link: ApolloLink.from([
      // On the server side, use SSRMultipartLink for handling multipart requests
      ...(typeof window === 'undefined'
        ? [new SSRMultipartLink({ stripDefer: true })]
        : []),
      // Add the authLink before the httpLink
      authLink,
      httpLink,
    ]),
    // Use new devtools option instead of deprecated connectToDevTools
    devtools: {
      enabled: process.env.NODE_ENV === 'development',
    },
  });
}

// you need to create a component to wrap your app in
export function ApolloClientProvider({ children }: React.PropsWithChildren) {
  return (
    <ApolloNextAppProvider makeClient={makeClient}>
      {children}
    </ApolloNextAppProvider>
  );
}
